import { memo, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { HeartIcon, TrashIcon, BellIcon, BellSlashIcon } from '@heroicons/react/24/outline'
import { HeartIcon as HeartIconSolid } from '@heroicons/react/24/solid'
import LoadingSpinner from '../../LoadingSpinner'
import CollapsibleSection from './CollapsibleSection'
import PaginationControls from './PaginationControls'
import { favoritesAPI } from '../../../services/api'
import { toast } from '../../../services/toastService'

/**
 * FavoritesSection - User's favorite entities display
 * Shows list of entities the user has marked as favorites with remove capability
 */
const FavoritesSection = memo(function FavoritesSection({
  isExpanded,
  onToggle,
  favorites,
  loading,
  currentPage,
  onPageChange,
  itemsPerPage,
  onRemove,
  onNotifyToggle,
  t
}) {
  const navigate = useNavigate()

  const handleNotifyToggle = useCallback(async (e, entityId, currentValue) => {
    e.stopPropagation()
    try {
      await favoritesAPI.toggleNotify(entityId)
      if (onNotifyToggle) onNotifyToggle(entityId, !currentValue)
      toast.success(!currentValue ? (t('favorites.notifyOn') || 'You\'ll be notified when spotted!') : (t('favorites.notifyOff') || 'Notifications off'))
    } catch {
      toast.error(t('toast.error.generic') || 'Something went wrong')
    }
  }, [onNotifyToggle, t])
  
  const totalPages = Math.ceil((favorites?.length || 0) / itemsPerPage)
  const paginatedFavorites = (favorites || []).slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  )

  return (
    <CollapsibleSection
      title={t('favorites.title')}
      icon={HeartIcon}
      count={favorites?.length || 0}
      isExpanded={isExpanded}
      onToggle={onToggle}
      tooltip={t('profile.tooltips.favoritesSection', 'Your favorite vendors. Get notified when they post updates or new locations.')}
      iconColor="text-pink-500"
      badgeColor="bg-pink-100 text-pink-700"
    >
      {loading ? (
        <LoadingSpinner size="sm" />
      ) : !favorites || favorites.length === 0 ? (
        <div className="p-3 text-center text-xs text-gray-500">
          {t('favorites.noFavorites')}
        </div>
      ) : (
        <>
          <div className="space-y-2 p-2">
            {paginatedFavorites.map((favorite) => {
              const entityId = favorite.entityId || favorite.entity?.id
              return (
                <div 
                  key={favorite.id} 
                  className="flex items-start gap-2.5 p-3 bg-white rounded-md border border-pink-200/60 hover:border-pink-300 hover:shadow-sm transition-all cursor-pointer group"
                  onClick={() => entityId && navigate(`/entity/${entityId}`)}
                >
                  <HeartIconSolid className="h-4 w-4 text-pink-500 flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900 break-words group-hover:text-primary transition-colors">
                      {favorite.entity?.name || favorite.entityName || 'Unknown'}
                    </p>
                    {(favorite.entity?.description || favorite.entityDescription) && (
                      <p className="text-xs text-gray-600 mt-1 line-clamp-2">
                        {favorite.entity?.description || favorite.entityDescription}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={(e) => handleNotifyToggle(e, entityId, favorite.notifyOnSpot || favorite.notify_on_spot)}
                    onTouchEnd={(e) => handleNotifyToggle(e, entityId, favorite.notifyOnSpot || favorite.notify_on_spot)}
                    className="flex-shrink-0 p-1.5 rounded transition-colors text-gray-400 hover:text-amber-500 hover:bg-amber-50"
                    title={favorite.notifyOnSpot || favorite.notify_on_spot ? t('favorites.notifyDisable') || 'Stop notifications' : t('favorites.notifyEnable') || 'Notify me when spotted'}
                  >
                    {favorite.notifyOnSpot || favorite.notify_on_spot
                      ? <BellIcon className="h-4 w-4 text-amber-500" />
                      : <BellSlashIcon className="h-4 w-4" />}
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      onRemove(entityId)
                    }}
                    className="flex-shrink-0 p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                    title={t('favorites.removeFromFavorites', 'Remove from favorites')}
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              )
            })}
          </div>
          <PaginationControls
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={onPageChange}
            t={t}
          />
        </>
      )}
    </CollapsibleSection>
  )
})

export default FavoritesSection
